import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PatientInitialStateType } from "DataTypes/Redux";
import {
  setEhrSyncSignal,
  setPatientEhrActiveSync,
} from "Redux/Patient/Action";
import { setAlertData } from "Redux/Screen/Action";
import EhrApi from "Service/Ehr.api";
import { ehrVendorDisplayName } from "Utils/EhrVendorMap";
import EhrSyncApi, {
  formatSyncTime,
  syncTimeKeyFor,
} from "Service/EhrSync.api";

type UseEhrSyncReturn = {
  /** Whether this domain's EHR Sync button should render at all. */
  canSync: boolean;
  /** True only while THIS domain is syncing, so one click spins one button. */
  syncing: boolean;
  /** Hover text naming the vendor, e.g. "Sync from Athena". */
  title: string;
  /**
   * This domain's last successful sync, already formatted;
   * "Never synced" when there is none.
   */
  lastSync: string;
  handleSync: () => Promise<void>;
};

/**
 * Drives a single "EHR Sync" button.
 *
 * Which domains exist is vendor-specific (Athena has no `vitals`, eCW has no
 * `cases`), so a button is shown purely on the strength of the capabilities
 * response - a component asks for its domain and gets back whether to render.
 *
 * This hook only runs the sync; it does not reload anything. On success it
 * announces the sync, and every section on screen that cares reloads itself
 * through useEhrRefresh. That split is what lets one "all" sync refresh every
 * visible section rather than only the one holding the button.
 *
 * @param domain  the domain this button syncs, from Constant/Ehr EhrDomains
 */
const useEhrSync: (domain: string) => UseEhrSyncReturn = (domain) => {
  const dispatch = useDispatch();
  const { patientInfo, ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient,
  );

  // No separate "loading" flag is needed: the initial state is enabled: false,
  // so nothing renders until the capabilities response actually lands.
  const canSync = Boolean(
    ehr?.enabled &&
    ehr?.syncDomains?.some((el) => el.domain == domain && el.enabled),
  );

  // The patient's last-sync times, shared across every button on the screen.
  //
  // getSyncTimes caches one in-flight request per patient, so nine section
  // buttons plus the card header cost one call rather than ten.
  // onSyncTimesChanged is what makes a button notice a sync it did not perform
  // itself -- a Sync All, or the same section synced from another card.
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const patientId = patientInfo?.id;

  useEffect(() => {
    if (!patientId || !domain) return undefined;
    let cancelled = false;
    const load: () => void = () => {
      EhrSyncApi.getSyncTimes(patientId).then((times) => {
        if (!cancelled) setLastSyncAt(times?.[syncTimeKeyFor(domain)] ?? null);
      });
    };
    load();
    const unsubscribe = EhrSyncApi.onSyncTimesChanged(load);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [patientId, domain]);

  const handleSync: () => Promise<void> = useCallback(async () => {
    dispatch(setPatientEhrActiveSync(domain));
    try {
      const res = await EhrApi.syncEHRDomains({
        id1: patientInfo.id,
        data: { domain },
      });
      if (res?.success) {
        // Wakes up every section on screen whose domain this covers.
        dispatch(setEhrSyncSignal(domain));
        // Re-reads the stamps and tells every mounted button, so the label
        // moves without a reload.
        if (patientInfo?.id) EhrSyncApi.refreshSyncTimes(patientInfo.id);
        // Only on success: commonFetch already dispatches the alert itself for
        // non-success responses, so alerting in both branches double-toasts.
        // The EHR service sends bare JSON that often carries no message, so
        // fall back to naming what actually happened.
        dispatch(
          setAlertData({
            alertMessage: res?.msg || "EHR sync completed",
            openAlert: true,
            alertVariant: "success",
          }),
        );
      }
    } finally {
      dispatch(setPatientEhrActiveSync(null));
    }
  }, [dispatch, domain, patientInfo.id]);

  // Named here rather than at each button so all of them stay in step, and
  // so the vendor is read from one place. The vendor is shown exactly as the
  // capabilities response sends it.
  // Shown exactly as the capabilities response names it, mapped to the name the
  // vendor uses for itself: "ATHENA" reads as athenahealth, "ECW" as eCW.
  const vendor = ehrVendorDisplayName(ehr?.vendor);

  return {
    canSync,
    syncing: ehr?.activeSyncDomain == domain,
    title: vendor
      ? `Sync this section from ${vendor}`
      : "Sync this section from the EHR",
    lastSync: formatSyncTime(lastSyncAt),
    handleSync,
  };
};

export default useEhrSync;
